import { useCallback, useEffect, useRef } from 'react';
import { isEmbeddingMessage, isValidMessage, createMessage } from '../utils/messaging';
import type { EmbeddingMessage, EmbeddingMessageType } from '../../../types/embedding';

interface UsePostMessageOptions {
  onMessage?: (message: EmbeddingMessage) => void;
  allowedOrigins?: string[];
}

interface UsePostMessageResult {
  sendMessage: (
    target: Window | HTMLIFrameElement | null,
    type: EmbeddingMessageType,
    payload: unknown,
    targetOrigin?: string
  ) => void;
}

export function usePostMessage({
  onMessage,
  allowedOrigins = ['*'],
}: UsePostMessageOptions = {}): UsePostMessageResult {
  const onMessageRef = useRef(onMessage);
  
  // Update ref when onMessage changes
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin
      if (allowedOrigins[0] !== '*' && !allowedOrigins.includes(event.origin)) {
        console.warn(`Blocked message from unauthorized origin: ${event.origin}`);
        return;
      }

      // Validate message structure
      if (!isValidMessage(event)) {
        return;
      }

      const message = event.data;
      if (isEmbeddingMessage(message)) {
        onMessageRef.current?.(message);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [allowedOrigins]);

  const sendMessage = useCallback(
    (
      target: Window | HTMLIFrameElement | null,
      type: EmbeddingMessageType,
      payload: unknown,
      targetOrigin: string = '*'
    ) => {
      if (!target) return;

      const message = createMessage(type, payload);
      
      let targetWindow: Window | null = null;
      if (target instanceof HTMLIFrameElement) {
        targetWindow = target.contentWindow;
      } else {
        targetWindow = target;
      }

      targetWindow?.postMessage(message, targetOrigin);
    },
    []
  );

  return { sendMessage };
}
